import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MISSIONS, COMPETITIONS, IMAGES } from '../mock/data';
import { Reveal, Eyebrow, ComingSoon, difficultyColor } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import { Users, Coins, MapPin, Gauge, ArrowRight, Swords, Map as MapIcon, CalendarClock, Home } from 'lucide-react';

const TABS = ['Available Missions', 'Solo Missions', 'Crew Missions', 'Competitions'];

function MissionCard({ m, i }) {
  const nav = useNavigate();
  const active = m.status === 'Available';
  const imgs = [IMAGES.wetStreet, IMAGES.rainyStreet, IMAGES.alleyCar, IMAGES.neonCity];
  return (
    <Reveal delay={i * 70}>
      <div className="xc-card rounded-2xl overflow-hidden">
        <div className="relative h-40">
          <img src={imgs[i % imgs.length]} alt={m.title} className={`h-full w-full object-cover ${!active ? 'grayscale opacity-60' : ''}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B10] via-[#0D0B10]/20 to-transparent" />
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className="text-[10px] font-600 tracking-[0.14em] uppercase px-2.5 py-1 rounded-full" style={{ color: difficultyColor(m.difficulty), border: `1px solid ${difficultyColor(m.difficulty)}55`, background: `${difficultyColor(m.difficulty)}14` }}>{m.difficulty}</span>
            {!active && <ComingSoon />}
          </div>
          <div className="absolute bottom-3 left-4 right-4">
            <h3 className="font-display font-700 text-xl text-white tracking-wide">{m.title}</h3>
            <div className="flex items-center gap-1 text-xs text-white/60 mt-1"><MapPin className="h-3 w-3 text-[#FF204E]" /> {m.location}</div>
          </div>
        </div>
        <div className="p-5">
          <p className="text-sm text-white/55 leading-relaxed min-h-[40px]">{m.blurb}</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-white/70"><Users className="h-4 w-4 text-[#B026FF]" /> {m.crew === 1 ? 'Solo — 1 player' : `${m.crew} players`}</div>
            <div className="flex items-center gap-2 text-sm text-[#E0B85A]"><Coins className="h-4 w-4" /> {m.reward.toLocaleString()} LC{m.crew === 1 ? '' : ' each'}</div>
          </div>
          {m.contribution && <div className="mt-2 flex items-center gap-2 text-xs text-white/45"><Gauge className="h-3.5 w-3.5" /> Max {m.contribution} qualifying rounds{m.crew === 1 ? '' : ' each'}</div>}
          <button onClick={() => active && m.route && nav(m.route)} disabled={!active}
            className={`mt-5 w-full rounded-lg py-3 text-sm font-600 flex items-center justify-center gap-2 ${active ? 'btn-neon' : 'btn-ghost opacity-60 cursor-not-allowed'}`}>
            {active ? <>VIEW MISSION <ArrowRight className="h-4 w-4" /></> : 'COMING SOON'}
          </button>
        </div>
      </div>
    </Reveal>
  );
}

function CompCard({ c, i, icon: Icon }) {
  const nav = useNavigate();
  const live = !!c.route;
  return (
    <Reveal delay={i * 80}>
      <div data-testid={`comp-card-${c.id}`} className="xc-card rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-20 blur-2xl" style={{ background: '#B026FF' }} />
        <div className="h-12 w-12 rounded-xl grid place-items-center mb-4" style={{ background: '#FF204E16', border: '1px solid #FF204E44' }}><Icon className="h-6 w-6 text-[#FF204E]" /></div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-display font-700 text-lg text-white">{c.title}</h3>
          {live ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-600 tracking-[0.18em] uppercase px-2.5 py-1 rounded-full border border-[#2FBF71]/40 text-[#2FBF71] bg-[#2FBF71]/8">Preview Live</span>
          ) : <ComingSoon />}
        </div>
        <p className="text-sm text-white/55 leading-relaxed">{c.desc}</p>
        {live && (
          <button data-testid={`comp-view-${c.id}`} onClick={() => nav(c.route)} className="btn-neon mt-5 rounded-lg px-5 py-2.5 text-sm font-600 flex items-center gap-2">
            VIEW PREVIEW <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </Reveal>
  );
}

export default function Missions() {
  const [tab, setTab] = useState(TABS[0]);
  const comps = [
    { ...COMPETITIONS[0], icon: Swords }, { ...COMPETITIONS[1], icon: MapIcon },
    { id: 'seasons', title: 'SEASONS', desc: 'New chapters, locations, rewards and missions each season.', icon: CalendarClock },
    { id: 'property', title: 'PROPERTY', desc: 'Build persistent status inside the city with owned property.', icon: Home },
  ];
  const list = MISSIONS.filter((m) => m.tab === tab);
  return (
    <div className="bg-[#070609] min-h-screen">
      <section className="relative overflow-hidden border-b border-white/5">
        <img src={IMAGES.rainyStreet} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 city-overlay" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 py-14">
          <Eyebrow>Mission Board</Eyebrow>
          <h1 className="font-display font-700 text-3xl sm:text-4xl md:text-5xl text-white tracking-tight">Assemble. Decide. <span className="text-gradient-rp">Execute.</span></h1>
          <p className="mt-3 text-white/60 max-w-xl">Cinematic multiplayer missions with real players. Joining is always optional — declining never harms you or your crew.</p>
          <div className="mt-4"><InvestorNote id="missions" title="Why missions are opt-in">
            Missions convert solitary casino sessions into shared appointments — the strongest known retention mechanic in multiplayer games. Keeping them opt-in (and never punishing refusal) means the social layer only ever adds pressure-free engagement, which also matters to regulators.
          </InvestorNote></div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
        <div className="flex gap-2 mb-8 border-b border-white/8">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-500 relative transition-colors ${tab === t ? 'text-white' : 'text-white/45 hover:text-white/75'}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#FF204E,#B026FF)' }} />}
            </button>
          ))}
        </div>

        {tab === 'Competitions' ? (
          <div className="grid sm:grid-cols-2 gap-5">{comps.map((c, i) => <CompCard key={c.id} c={c} i={i} icon={c.icon} />)}</div>
        ) : list.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{list.map((m, i) => <MissionCard key={m.id} m={m} i={i} />)}</div>
        ) : (
          <div className="text-center py-20 text-white/40">No missions in this category yet.</div>
        )}
      </section>
    </div>
  );
}
